import type { RouteModel } from '@polaris/core/models'
import { featureFlagsAtom } from '@polaris/core/stores'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useTimeSpanGroupedHistory } from '@/src/queries/hooks/history'
import { accountsAddressesAtom } from '@/src/stores/accounts/atoms'

/**
 * Hook to get the most recent failed trades from activity history
 * @returns {RouteModel[]} Array of recent failed trades (up to 3) or empty array if none found
 */
export function useMostRecentFailedTrade(): RouteModel[] {
  const featureFlags = useAtomValue(featureFlagsAtom)
  const accountsAddresses = useAtomValue(accountsAddressesAtom)

  const groupedHistoryQuery = useTimeSpanGroupedHistory({
    wallets: accountsAddresses,
    hideExpired: true,
    enabled: accountsAddresses.length > 0 && featureFlags.abortNotifications,
    type: 'failed', // Only get failed trades
  })

  const { data: groupedActivity } = groupedHistoryQuery

  const recentFailedTrades = useMemo(() => {
    // Return empty array if feature flag is disabled
    if (!featureFlags.abortNotifications) {
      return []
    }

    if (!groupedActivity?.length) return []

    // Find all failed routes that are not date headers
    const failedRoutes = groupedActivity.filter(
      (item): item is RouteModel =>
        typeof item !== 'string' && item.status.type === 'failed',
    )

    // Return the last 3 failed trades (they're already sorted by time, most recent first)
    return failedRoutes.slice(0, 3)
  }, [groupedActivity, featureFlags.abortNotifications])

  return recentFailedTrades
}
